import JSBI from 'jsbi'
import {
  AggregatedQuoteResult,
  AggregatorQuote,
  ProviderFailure,
  QuoteProvider,
  QuoteRequest,
} from './types'

/**
 * Provider registry and best-quote selection.
 *
 * Two properties matter more than anything else here:
 *
 * 1. **One bad source must not blank the swap screen.** Aggregators rate-limit, time
 *    out and return no-route constantly. Every provider is isolated; failures are
 *    collected and reported, and any successful quote still wins.
 * 2. **Best price is decided on guaranteed output, not headline output.** Comparing
 *    `buyAmount` alone rewards a source that quotes optimistically and then slips.
 *    `minBuyAmount` is what the user is actually guaranteed to receive.
 */

const providers: QuoteProvider[] = []

export function registerProvider(provider: QuoteProvider): void {
  const existing = providers.findIndex((p) => p.id === provider.id)
  if (existing >= 0) providers[existing] = provider
  else providers.push(provider)
}

export function clearProviders(): void {
  providers.length = 0
}

export function getProviders(): QuoteProvider[] {
  return [...providers]
}

/** Providers that can actually serve this chain right now. */
export function getEligibleProviders(chainId: number): QuoteProvider[] {
  return providers.filter((p) => p.supportsChain(chainId) && p.isConfigured())
}

function toBig(value: string | undefined): JSBI {
  if (!value) return JSBI.BigInt(0)
  try {
    return JSBI.BigInt(value)
  } catch {
    return JSBI.BigInt(0)
  }
}

/** Ranking key: what the user is guaranteed, falling back to the expected amount. */
function guaranteedOutput(quote: AggregatorQuote): JSBI {
  const min = toBig(quote.minBuyAmount)
  return JSBI.greaterThan(min, JSBI.BigInt(0)) ? min : toBig(quote.buyAmount)
}

function describeFailure(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'object' && error !== null) {
    const { message } = error as { message?: unknown }
    if (typeof message === 'string' && message.trim()) return message.trim()
  }
  return 'Quote failed'
}

/**
 * Basis-points improvement of `best` over `runnerUp`.
 * This is the number that evidences routing quality, so it is computed rather than
 * asserted: (best - runnerUp) / runnerUp * 10000.
 */
export function improvementBps(best: AggregatorQuote, runnerUp: AggregatorQuote): number | undefined {
  const b = guaranteedOutput(best)
  const r = guaranteedOutput(runnerUp)
  if (JSBI.lessThanOrEqual(r, JSBI.BigInt(0))) return undefined

  const delta = JSBI.subtract(b, r)
  const scaled = JSBI.divide(JSBI.multiply(delta, JSBI.BigInt(10_000)), r)
  return JSBI.toNumber(scaled)
}

export interface FetchQuotesOptions {
  /** Per-provider timeout. A slow source must not hold up the whole quote. */
  timeoutMs?: number
  signal?: AbortSignal
}

const DEFAULT_TIMEOUT_MS = 8_000

function withTimeout(
  provider: QuoteProvider,
  request: QuoteRequest,
  timeoutMs: number,
  outerSignal?: AbortSignal
): Promise<AggregatorQuote> {
  const controller = new AbortController()
  const onAbort = () => controller.abort()

  if (outerSignal) {
    if (outerSignal.aborted) controller.abort()
    else outerSignal.addEventListener('abort', onAbort)
  }

  const timer = setTimeout(() => controller.abort(), timeoutMs)

  return provider
    .fetchQuote(request, controller.signal)
    .catch((error) => {
      // An abort here is a timeout unless the caller cancelled, in which case the
      // caller already knows and the message is never shown.
      if (controller.signal.aborted && !outerSignal?.aborted) {
        throw new Error(`${provider.label} timed out`)
      }
      throw error
    })
    .finally(() => {
      clearTimeout(timer)
      outerSignal?.removeEventListener('abort', onAbort)
    })
}

/**
 * Quote across every eligible source in parallel and return them ranked.
 *
 * Never rejects because a provider failed - only the caller's own abort propagates.
 */
export async function fetchBestQuote(
  request: QuoteRequest,
  options: FetchQuotesOptions = {}
): Promise<AggregatedQuoteResult> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, signal } = options
  const eligible = getEligibleProviders(request.chainId)

  if (eligible.length === 0) {
    return {
      best: null,
      quotes: [],
      failures: [
        {
          providerId: 'registry',
          providerLabel: 'Routing',
          message: `No routing source is configured for chain ${request.chainId}.`,
        },
      ],
    }
  }

  const settled = await Promise.all(
    eligible.map((provider) =>
      withTimeout(provider, request, timeoutMs, signal).then(
        (quote) => ({ ok: true as const, quote }),
        (error) => ({
          ok: false as const,
          failure: {
            providerId: provider.id,
            providerLabel: provider.label,
            message: describeFailure(error),
          } as ProviderFailure,
        })
      )
    )
  )

  const quotes: AggregatorQuote[] = []
  const failures: ProviderFailure[] = []

  settled.forEach((result) => {
    if (result.ok) quotes.push(result.quote)
    else failures.push(result.failure)
  })

  // A zero-output quote is a no-route dressed up as a success; treat it as a failure
  // so it can never win the comparison.
  const usable = quotes.filter((q) => JSBI.greaterThan(guaranteedOutput(q), JSBI.BigInt(0)))
  quotes
    .filter((q) => !usable.includes(q))
    .forEach((q) =>
      failures.push({ providerId: q.providerId, providerLabel: q.providerLabel, message: 'No route found' })
    )

  usable.sort((a, b) => (JSBI.greaterThan(guaranteedOutput(a), guaranteedOutput(b)) ? -1 : 1))

  if (usable.length === 0) return { best: null, quotes: [], failures }

  const [winner, runnerUp] = usable
  return {
    best: { ...winner, improvementBps: runnerUp ? improvementBps(winner, runnerUp) : undefined },
    quotes: usable,
    failures,
  }
}
